import { Link } from '@tanstack/react-router';

import { GarmentMockup } from '@/components/garment-mockup';
import { accentTextStyle } from '@/lib/accent';
import type { CatalogueItem } from '@/lib/data';
import { formatCents } from '@/lib/money';

/**
 * A product in the storefront grid.
 *
 * The sheet's rule for this grid, in its own words: **the garment reads as the
 * object and the card doesn't.** So there is no panel, no shadow and no lift on
 * hover. A 4/5 portrait frame at 4px radius, 12px under it, and three lines of
 * type: name in sans 500, the colour in muted sans, the price in the same mono
 * tabular numerals the back office uses.
 *
 * The card used to be a bordered panel that rose half a pixel on hover, with
 * the brand name in extrabold uppercase and the price at 18px bold. That is
 * four things competing with the photograph they surround. R-190.
 */
export function ProductCard({ item }: { item: CatalogueItem }) {
  const outOfStock = item.fulfilment === 'stock' && (item.stock_qty ?? 0) <= 0;

  return (
    <Link to="/product/$id" params={{ id: item.id }} className="reveal group block">
      <div className="relative mb-3 aspect-[4/5] overflow-hidden rounded-[4px] border border-line bg-raised">
        <GarmentMockup
          type={item.type}
          colorHex={item.color_hex}
          designUrl={item.design_url}
          placement={{
            pos_x: item.pos_x ?? 50,
            pos_y: item.pos_y ?? 48,
            scale: item.scale ?? 60,
          }}
          label={`${item.title} in ${item.color_name}`}
          className="h-full w-full"
        />
        {/* One of the three places the yellow is allowed to appear on the whole
            site. Ink on it is 11.6:1. */}
        {outOfStock && (
          <span className="absolute left-2.5 top-2.5 rounded-[4px] bg-[color:var(--color-brand)] px-2 py-1 font-[family-name:var(--font-mono)] text-[10px] font-medium uppercase tracking-[0.1em] text-[color:var(--color-on-brand)]">
            Sold out
          </span>
        )}
      </div>

      <span
        className="accent-text font-[family-name:var(--font-mono)] text-[10px] uppercase tracking-[0.1em]"
        style={accentTextStyle(item.brand.accent_hex)}
      >
        {item.brand.name}
      </span>
      <h3 className="mt-1 text-[13.5px] font-medium leading-[1.3] group-hover:underline">
        {item.title}
      </h3>
      <p className="mt-0.5 text-[11.5px] leading-[1.3] text-muted">{item.color_name}</p>
      <p className="mt-1.5 font-[family-name:var(--font-mono)] text-[13px] font-medium tabular-nums">
        {formatCents(item.retail_price_cents)}
      </p>
    </Link>
  );
}
